import React, { StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN } from '@fs/zion-config'
import { getSentryConfig } from '@fs/tree-config'
import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'

const EmptyLoading = (): JSX.Element => <div />

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to zion's mergeSentryConfig via babylon's common tree-config

  // Docs: https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/
  Sentry.init(
    getSentryConfig({
      // If we really need specific entries for ignoreErrors, allowUrls, or denyUrls, consider having them added to the tree-config to be applied to all Tree apps, or to zion-config to benefit the entire org.
      ignoreErrors: [],
      allowUrls: [],
      denyUrls: [],
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACING_RATE ?? '') || 1, // .05 = 5% of errors sent (please note that all errors will be up to 20x more severe than the Sentry dashboard will indicate)
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)

  return (
    <Router basename={base} handledExternalRoutes={['/groups']}>
      <Root
        // We position the `main` element inside the App component, since we have a subnav that we want to be outside of it. That same `main` element has an id of skipToMainContent so that the skip link works correctly.
        skipLinkId="skipToMainContent"
        as="div"
        analytics
      >
        <App />
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={EmptyLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
